import { useState } from 'react';

/**
 * useChatSelection — manages per-message selection mode, the active action
 * menu (which bubble shows Edit/Delete/Select pills), and delete modal state.
 *
 * Returns everything ChatPage needs to pass down to ChatBubble and the
 * SelectionHeader / DeleteConfirmModal components.
 */
const useChatSelection = (deleteMessageFn) => {
  // ── Active action menu (single bubble showing pill buttons) ──────────────
  const [activeActionMessageId, setActiveActionMessageId] = useState(null);

  // ── Multi-select state ────────────────────────────────────────────────────
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());

  // ── Delete modal state ────────────────────────────────────────────────────
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteModalType, setDeleteModalType] = useState('single'); // 'single' | 'bulk'
  const [singleDeleteId, setSingleDeleteId] = useState(null);

  // ── Action-menu toggle ────────────────────────────────────────────────────
  const handleToggleActions = (messageId) => {
    setActiveActionMessageId(prev => (prev === messageId ? null : messageId));
  };

  // ── Single delete trigger (opens modal) ──────────────────────────────────
  const handleDelete = (messageId) => {
    setActiveActionMessageId(null);
    setSingleDeleteId(messageId);
    setDeleteModalType('single');
    setIsDeleteModalOpen(true);
  };

  // ── Long-press → enter select mode ───────────────────────────────────────
  const handleLongPress = (messageId) => {
    setIsSelectMode(true);
    setActiveActionMessageId(messageId);
    setSelectedMessageIds(new Set([messageId]));
  };

  // ── Toggle individual message selection ───────────────────────────────────
  const handleSelectToggle = (messageId) => {
    setSelectedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
        if (next.size === 0) {
          setIsSelectMode(false);
          setActiveActionMessageId(null);
        }
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  // ── Exit select mode entirely ─────────────────────────────────────────────
  const exitSelectMode = () => {
    setIsSelectMode(false);
    setActiveActionMessageId(null);
    setSelectedMessageIds(new Set());
  };

  // ── Bulk delete trigger (opens modal) ────────────────────────────────────
  const handleMassDeleteClick = () => {
    if (selectedMessageIds.size < 2) return;
    setDeleteModalType('bulk');
    setIsDeleteModalOpen(true);
  };

  // ── Confirm delete (executes actual deletion) ─────────────────────────────
  const confirmDelete = async () => {
    setIsDeleteModalOpen(false);
    setActiveActionMessageId(null);

    if (deleteModalType === 'single') {
      if (singleDeleteId) {
        await deleteMessageFn(singleDeleteId);
        setSingleDeleteId(null);
      }
    } else if (deleteModalType === 'bulk') {
      const ids = Array.from(selectedMessageIds);
      for (const msgId of ids) {
        await deleteMessageFn(msgId);
      }
      exitSelectMode();
    }
  };

  return {
    // State
    activeActionMessageId,
    isSelectMode,
    selectedMessageIds,
    isDeleteModalOpen,
    deleteModalType,

    // Handlers
    handleToggleActions,
    handleDelete,
    handleLongPress,
    handleSelectToggle,
    exitSelectMode,
    handleMassDeleteClick,
    confirmDelete,
    setIsDeleteModalOpen,
  };
};

export default useChatSelection;
